import { CalendarDays } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { useEmbedRuntime } from "~/context/EmbedRuntimeContext";
import { useMatrix } from "~/context/MatrixContext";
import { useToast } from "~/context/ToastContext";
import {
  InstantMeetingError,
  cachedOpenIdProvider,
  mintJoinToken,
  type TokenProvider,
} from "~/lib/meet-instant";
import { openMeeting } from "~/lib/meet-link";
import { clearMeetingActive, requestOpenIdToken, type MeetingPresence } from "~/lib/matrix";
import { ICON_STROKE } from "~/lib/icon";

interface MeetingBannerProps {
  meeting: MeetingPresence;
}

function joinErrorMessage(kind: InstantMeetingError["kind"]): string {
  switch (kind) {
    case "unauthorized":
      return "Sign in to Nurion to join this meeting.";
    case "forbidden":
      return "You don't have access to this meeting.";
    case "gone":
      return "This meeting has ended.";
    default:
      return "Could not join the meeting. Try again.";
  }
}

// One in-flight mint per meeting, shared across every entry point (the
// banner callout and the room-header pill), so neither can open two tabs.
const inflight = new Set<string>();

export function useJoinMeeting(
  meeting: MeetingPresence | null,
  getToken: TokenProvider,
): {
  join: () => Promise<void>;
  busy: boolean;
  error: string | null;
} {
  const { meetOrigins } = useEmbedRuntime();
  const { push: pushToast } = useToast();
  const matrix = useMatrix();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const join = useCallback(async () => {
    if (!meeting || busy || inflight.has(meeting.meetingId)) return;
    setBusy(true);
    setError(null);
    inflight.add(meeting.meetingId);
    try {
      const joinUrl = await mintJoinToken(meeting.meetingId, meeting.joinCapability, getToken);
      await openMeeting(joinUrl, pushToast, meetOrigins);
    } catch (err) {
      if (err instanceof InstantMeetingError) {
        pushToast({ message: joinErrorMessage(err.kind), tone: "danger" });
        if (err.kind === "gone") {
          const client = matrix?.client;
          if (client) {
            try {
              await clearMeetingActive(client, meeting.matrixRoomId);
            } catch (cleared) {
              console.warn("clearing meeting state failed", cleared);
            }
          }
        }
        return;
      }
      setError("Could not open the meeting. Try again.");
    } finally {
      inflight.delete(meeting.meetingId);
      setBusy(false);
    }
  }, [meeting, busy, pushToast, meetOrigins, getToken, matrix]);

  return { join, busy, error };
}

export function MeetingBanner({ meeting }: MeetingBannerProps) {
  const matrix = useMatrix();
  const openIdProvider = useMemo(
    () =>
      cachedOpenIdProvider(async () => {
        if (!matrix?.client) throw new Error("no matrix client");
        return requestOpenIdToken(matrix.client);
      }),
    [matrix],
  );
  const { join, busy, error } = useJoinMeeting(meeting, openIdProvider);
  const started = relativeStarted(meeting.startedAt);
  return (
    <div
      style={{
        height: 48,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-3)",
        padding: "0 var(--chat-space-4)",
        background: "var(--chat-surface-raised)",
        color: "var(--chat-fg-primary)",
        fontFamily: "var(--chat-font-sans)",
      }}
    >
      <CalendarDays
        aria-hidden
        size={20}
        strokeWidth={ICON_STROKE}
        style={{ color: "var(--chat-fg-secondary)", flexShrink: 0 }}
      />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: "var(--chat-type-body-md-size)",
            lineHeight: "var(--chat-type-body-md-line)",
            fontWeight: 500,
          }}
        >
          Meeting in progress
        </div>
        <div
          style={{
            color: "var(--chat-fg-secondary)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {meeting.title} · started {started}
          {error ? ` · ${error}` : ""}
        </div>
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          void join();
        }}
        style={{
          height: 32,
          padding: "0 var(--chat-space-3)",
          border: "none",
          borderRadius: "var(--chat-radius-md)",
          background: "var(--chat-cta-bg)",
          color: "var(--chat-cta-fg)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-sm-size)",
          lineHeight: "var(--chat-type-body-sm-line)",
          fontWeight: 500,
          cursor: busy ? "wait" : "pointer",
          flexShrink: 0,
        }}
      >
        Join meeting
      </button>
    </div>
  );
}

function relativeStarted(value: string): string {
  const ts = new Date(value).getTime();
  if (!Number.isFinite(ts)) return "now";
  const elapsedMs = Date.now() - ts;
  if (elapsedMs < 60_000) return "now";
  if (elapsedMs < 60 * 60_000) {
    const minutes = Math.round(elapsedMs / 60_000);
    return minutes === 1 ? "1 min ago" : `${minutes} min ago`;
  }
  const totalHours = elapsedMs / 3_600_000;
  if (totalHours < 24) return `${Math.floor(totalHours)} h ago`;
  return `${Math.floor(elapsedMs / 86_400_000)} d ago`;
}
